import Image from '@/models/image';
import Ingredient from '@/models/ingredient';
import StorehouseUnitInterface from '@/models/storehouseUnitInterface';
import stringHelper from '@/utils/stringHelper';

export default class StorehouseIngredient extends Ingredient implements StorehouseUnitInterface {
  public maxRemainder: number = 0;
  public remainder: number = 0;

  public static createFromRequest(ingredientProps: any): StorehouseIngredient {
    const ingredient = new StorehouseIngredient();

    ingredient.cost = ingredientProps.cost;

    if (ingredientProps.count) {
      ingredient.count = ingredientProps.count;
    }

    ingredient.id = ingredientProps.id;

    if (ingredientProps.image) {
      ingredient.image = new Image(ingredientProps.image);
    }

    ingredient.name = ingredientProps.name;
    ingredient.description = ingredientProps.description;
    ingredient.maxRemainder = ingredientProps.maxRemainder;
    ingredient.posCode = ingredientProps.posCode;
    ingredient.remainder = ingredientProps.remainder;
    ingredient.unit = ingredientProps.unit;
    ingredient.unitCount = ingredientProps.unitCount;

    return ingredient;
  }

  public get countPercent() {
    return (this.count > this.maxCount ? this.maxCount : this.count) * 100 / this.maxRemainder;
  }

  public get writeOffCountPercent() {
    const writeOffCountPercent = (this.count > this.remainder ? this.remainder : this.count) * 100 / (this.remainder < this.maxRemainder ? this.maxRemainder : this.remainder);

    if (writeOffCountPercent < 0) {
      return 0;
    }

    if (writeOffCountPercent > 100) {
      return 100;
    }

    return writeOffCountPercent;
  }

  public get maxCount() {
    return this.maxRemainder - this.remainder;
  }

  public get remainderPercent() {
    const remainder = this.remainder < 0 ? 0 : this.remainder;

    if (remainder > this.maxRemainder) {
      return 100;
    }

    return remainder * 100 / this.maxRemainder;
  }

  public get isSuccess() {
    return !this.isDanger && !this.isWarning;
  }

  public get isWarning() {
    return !this.isDanger && (this.remainder < this.maxRemainder / 2);
  }

  public get isDanger() {
    return this.remainder < this.maxRemainder / 4;
  }

  public isEqualSearchText(searchText: string = ''): boolean {
    searchText = searchText.toLocaleLowerCase();

    if (searchText.length === 0) {
      return true;
    }

    return this.name.toLocaleLowerCase().includes(searchText);
  }

  public compareName(storehouseUnit: StorehouseUnitInterface): number {
    return stringHelper.sortStrings(this.getName(), storehouseUnit.getName());
  }

  public compareCount(storehouseUnit: StorehouseUnitInterface): number {
    return this.getRemainder() > storehouseUnit.getRemainder() ? -1 : 1;
  }

  public getName(): string {
    return this.name;
  }

  public getRemainder(): number {
    return this.remainder;
  }

  public getRemainderDeviation(): number {
    const minNormal = this.maxRemainder / 4;

    if (this.remainder > this.maxRemainder) {
      const remainderDeviation = this.remainder - this.maxRemainder;

      if (remainderDeviation > minNormal) {
        return remainderDeviation - minNormal;
      }
    }

    if (this.remainder < minNormal) {
      return minNormal - this.remainder;
    }

    return 0;
  }
}
